import { ApiError, errorFromResponse, toApiError } from "./errors";
import type { SessionClaims, SessionGrant } from "../../types/session";

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export interface ApiClientOptions {
  baseUrl: string;
  fetch?: FetchLike;
}

/**
 * 제어 평면 타입 클라이언트. 쿠키를 보내지 않는다(credentials: "omit", ADR-B06 쿠키리스).
 * 모든 실패는 ApiError(FE 코드 또는 NET-*)로 던진다.
 */
export interface ApiClient {
  exchange(ticket: string): Promise<SessionGrant>;
  campusSso(bffToken: string): Promise<SessionGrant>;
  refresh(token: string): Promise<SessionGrant>;
  me(token: string): Promise<SessionClaims>;
  /** 인증이 필요한 일반 호출(S2~ 에서 사용). `as: "bytes"` 면 본문을 Uint8Array 로(S4 리플레이 파일) */
  request<T>(
    path: string,
    init: RequestInit & { token?: string; as?: "json" | "bytes" },
  ): Promise<T>;
}

const ROLES = new Set(["student", "teacher", "operator", "org_admin", "observer"]);
const BANDS = new Set(["SE", "SU", null]);

function assertClaims(v: unknown): asserts v is SessionClaims {
  const o = v as Record<string, unknown> | null;
  const ok =
    !!o &&
    typeof o.expiresAt === "string" &&
    !Number.isNaN(Date.parse(o.expiresAt)) &&
    ROLES.has(o.role as string) &&
    typeof o.courseId === "string" &&
    typeof o.pid === "string" &&
    typeof o.displayName === "string" &&
    BANDS.has((o.gradeBand ?? null) as string | null) &&
    typeof o.locale === "string";
  if (!ok) throw new ApiError("NET-SERVER", 200, "세션 응답 형식이 계약과 다릅니다");
}

function assertGrant(v: unknown): asserts v is SessionGrant {
  assertClaims(v);
  if (typeof (v as { token?: unknown }).token !== "string" || !(v as SessionGrant).token)
    throw new ApiError("NET-SERVER", 200, "세션 토큰이 없습니다");
}

const normalize = <T extends SessionClaims>(v: T): T => ({ ...v, gradeBand: v.gradeBand ?? null });

export function createApiClient({ baseUrl: rawBase, fetch: f }: ApiClientOptions): ApiClient {
  const baseUrl = rawBase.replace(/\/+$/, "");
  const doFetch: FetchLike = f ?? ((i, init) => globalThis.fetch(i, init));

  async function request<T>(
    path: string,
    init: RequestInit & { token?: string; as?: "json" | "bytes" },
  ): Promise<T> {
    const { token, headers, as = "json", ...rest } = init;
    const h = new Headers(headers);
    // 호출부가 Accept 를 정했으면 그것(P2-S6 CSV 내보내기 — text/csv)
    if (!h.has("Accept"))
      h.set("Accept", as === "bytes" ? "application/octet-stream" : "application/json");
    // FormData(멀티파트 — S4 리플레이 업로드)는 브라우저가 경계를 넣은 Content-Type 을 스스로 단다
    const multipart = typeof FormData !== "undefined" && rest.body instanceof FormData;
    if (rest.body != null && !multipart && !h.has("Content-Type"))
      h.set("Content-Type", "application/json");
    if (token) h.set("Authorization", `Bearer ${token}`);
    let res: Response;
    try {
      res = await doFetch(`${baseUrl}${path}`, { ...rest, headers: h, credentials: "omit" });
    } catch (e) {
      throw toApiError(e);
    }
    if (as === "bytes" && res.ok) {
      const buf = await res.arrayBuffer().catch(() => null);
      if (!buf) throw new ApiError("NET-OFFLINE", 0, "본문을 받지 못했습니다");
      return new Uint8Array(buf) as T;
    }
    let body: unknown = null;
    const text = await res.text().catch(() => "");
    if (text) {
      try {
        body = JSON.parse(text);
      } catch {
        body = null;
      }
    }
    if (!res.ok) throw errorFromResponse(res.status, body);
    return body as T;
  }

  const grant = async (path: string, init: RequestInit & { token?: string }) => {
    const v = await request<unknown>(path, init);
    assertGrant(v);
    return normalize(v);
  };

  return {
    exchange: (ticket) =>
      grant("/api/session/exchange", { method: "POST", body: JSON.stringify({ ticket }) }),
    campusSso: (bffToken) =>
      grant("/api/session/campus-sso", { method: "POST", body: JSON.stringify({ bffToken }) }),
    refresh: (token) => grant("/api/session/refresh", { method: "POST", token }),
    me: async (token) => {
      const v = await request<unknown>("/api/me", { method: "GET", token });
      assertClaims(v);
      return normalize(v);
    },
    request,
  };
}
